"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { useParams, useRouter } from "next/navigation";
import { Eraser, House, RotateCcw, Undo2, X } from "lucide-react";
import { AsyncSearchCombobox } from "@/components/ui/AsyncSearchCombobox";
import { CommandGroup, CommandItem } from "@/components/ui/Command";
import { cn } from "@/utils/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { NAV_SEARCH_TTL_MS, type RecentSelection, useNavSearchStore } from "@/store/useNavSearchStore";
import { toSnakeCase } from "@app/utils";
import { SEARCH_MODULES, type SearchEntityDestination, type ModuleId, type SearchEntity, type SearchModule } from "./search-modules";
import { buildGlobalSearchGroups } from "./build-global-search-groups";
import { isHintResultItem, type SearchResultItem } from "./global-search.types";
type Props = {
  className?: string;
  placeholder?: string;
};
function getStickyActionMeta(actionId: string) {
  switch (actionId) {
    case "open-module-landing":
      return {
        icon: House,
        className: "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 dark:border-blue-500/40 dark:bg-blue-500/15 dark:text-blue-200 dark:hover:bg-blue-500/25 dark:hover:border-blue-400/50"
      };
    case "clear-selected-entity":
      return {
        icon: Undo2,
        className: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:border-amber-300 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200 dark:hover:bg-amber-500/25 dark:hover:border-amber-400/50"
      };
    case "clear-search-state":
      return {
        icon: RotateCcw,
        className: "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 hover:border-rose-300 dark:border-rose-500/40 dark:bg-rose-500/15 dark:text-rose-200 dark:hover:bg-rose-500/25 dark:hover:border-rose-400/50"
      };
    case "clear-query":
    default:
      return {
        icon: Eraser,
        className: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-200 dark:hover:bg-emerald-500/25 dark:hover:border-emerald-400/50"
      };
  }
}
function normalize(value: string) {
  return value.trim().toLowerCase();
}
function includesNormalized(haystack: string, needle: string) {
  const h = normalize(haystack);
  const n = normalize(needle);
  if (!n) return true;
  return h.includes(n);
}
function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function HighlightMatch(t0) {
  const $ = _c(8);
  const {
    text,
    query
  } = t0;
  let t1;
  let t2;
  if ($[0] !== query || $[1] !== text) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const normalizedQuery = query.trim();
      if (!normalizedQuery) {
        let t3;
        if ($[4] !== text) {
          t3 = <>{text}</>;
          $[4] = text;
          $[5] = t3;
        } else {
          t3 = $[5];
        }
        t2 = t3;
        break bb0;
      }
      const regex = new RegExp(`(${escapeRegExp(normalizedQuery)})`, "ig");
      const parts = text.split(regex);
      t1 = parts.map((part, index) => {
        const isMatch = part.toLowerCase() === normalizedQuery.toLowerCase();
        if (!isMatch) {
          return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
        }
        return <span key={`${part}-${index}`} className="rounded-sm bg-amber-200 px-0.5 font-semibold text-amber-900 dark:bg-amber-200/90 dark:text-amber-950">{part}</span>;
      });
    }
    $[0] = query;
    $[1] = text;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[6] !== t1) {
    t3 = <>{t1}</>;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
const EMPTY_SEARCH_ENTITIES: SearchEntity[] = [];
const ENTITY_RESULTS_BATCH_SIZE = 10;
const ENTITY_RESULTS_SCROLL_THRESHOLD = 32;
const FREQUENT_ENTITIES_LIMIT = 5;
type RecentEntitySelection = Extract<RecentSelection, {
  kind: "entity";
}>;
export const GlobalSearch = React.forwardRef<HTMLInputElement, Props>(({
  className,
  placeholder = "Select a module, then search an item…"
}, ref) => {
  const router = useRouter();
  const params = useParams();
  const branchId = typeof params?.branch_id === "string" ? params.branch_id : "";
  const hasPermission = useAuthStore(s => s.hasPermission);
  const canReadPermission = React.useCallback((path: string, module: string) => Boolean(branchId && hasPermission(branchId, toSnakeCase(path), toSnakeCase(module), "read")), [branchId, hasPermission]);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [selectedModuleId, setSelectedModuleId] = React.useState<ModuleId | null>(null);
  const [selectedEntity, setSelectedEntity] = React.useState<SearchEntity | null>(null);
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [entityResultsLimit, setEntityResultsLimit] = React.useState(ENTITY_RESULTS_BATCH_SIZE);
  const [isMacLikePlatform, setIsMacLikePlatform] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const setInputRef = React.useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === "function") {
      ref(node);
      return;
    }
    if (ref) {
      ref.current = node;
    }
  }, [ref]);
  const focusInput = React.useCallback(() => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);
  React.useEffect(() => {
    const onGlobalKeyDown = (event: KeyboardEvent) => {
      const isSearchHotkey = (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "k";
      if (!isSearchHotkey) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isSearchInputTarget = Boolean(target) && target === inputRef.current;
      const isTypingTarget = Boolean(target?.isContentEditable) || tagName === "input" || tagName === "textarea" || tagName === "select";
      if (isTypingTarget && !isSearchInputTarget) return;
      event.preventDefault();
      setOpen(true);
      focusInput();
    };
    window.addEventListener("keydown", onGlobalKeyDown);
    return () => {
      window.removeEventListener("keydown", onGlobalKeyDown);
    };
  }, [focusInput]);
  React.useEffect(() => {
    setIsMacLikePlatform(/Mac|iPhone|iPad|iPod/i.test(navigator.platform));
  }, []);
  const queryDebounced = useDebouncedValue(query, 250);
  const moduleFrequency = useNavSearchStore(s_0 => s_0.moduleFrequency);
  const entityFrequency = useNavSearchStore(s_1 => s_1.entityFrequency);
  const recentSelections = useNavSearchStore(s_2 => s_2.recentSelections);
  const entityKey = useNavSearchStore(s_3 => s_3.entityKey);
  const getCacheEntry = useNavSearchStore(s_4 => s_4.getCacheEntry);
  const isCacheFresh = useNavSearchStore(s_5 => s_5.isCacheFresh);
  const setCachedEntities = useNavSearchStore(s_6 => s_6.setCachedEntities);
  const bumpModule = useNavSearchStore(s_7 => s_7.bumpModule);
  const bumpEntity = useNavSearchStore(s_8 => s_8.bumpEntity);
  const pushRecent = useNavSearchStore(s_9 => s_9.pushRecent);
  const pruneExpired = useNavSearchStore(s_10 => s_10.pruneExpired);
  const selectedModule = React.useMemo(() => SEARCH_MODULES.find(m => m.id === selectedModuleId) ?? null, [selectedModuleId]);
  const selectedModuleCacheEntry = selectedModule ? getCacheEntry(selectedModule.id) : undefined;
  const selectedModuleItems = selectedModuleCacheEntry?.items ?? EMPTY_SEARCH_ENTITIES;
  const selectedModuleCachedCount = selectedModuleItems.length;
  const entityDestinations = React.useMemo(() => {
    if (!selectedModule?.entityDestinations) return [];
    return selectedModule.entityDestinations.filter(destination => {
      if (!destination.permission) return true;
      return canReadPermission(destination.permission.path, destination.permission.module);
    });
  }, [canReadPermission, selectedModule]);
  const visibleEntityDestinations = React.useMemo(() => {
    if (!selectedEntity) return [];
    const q = normalize(queryDebounced);
    if (!q) return entityDestinations;
    return entityDestinations.filter(destination_0 => includesNormalized(destination_0.label, q) || includesNormalized(destination_0.description, q));
  }, [entityDestinations, queryDebounced, selectedEntity]);
  const visibleModules = React.useMemo(() => {
    const q_0 = normalize(queryDebounced);
    const base = SEARCH_MODULES.filter(m_0 => {
      if (m_0.modulePermission) {
        if (!canReadPermission(m_0.modulePermission.path, m_0.modulePermission.module)) {
          return false;
        }
      }
      return includesNormalized(m_0.label, q_0) || includesNormalized(m_0.description, q_0) || !q_0;
    });
    return base.sort((a, b) => {
      const fa = moduleFrequency[a.id] ?? 0;
      const fb = moduleFrequency[b.id] ?? 0;
      if (fa !== fb) return fb - fa;
      return a.label.localeCompare(b.label);
    });
  }, [canReadPermission, queryDebounced, moduleFrequency]);
  const frequentModules = React.useMemo(() => {
    const q_1 = normalize(queryDebounced);
    if (q_1) return [];
    return visibleModules.filter(m_1 => (moduleFrequency[m_1.id] ?? 0) > 0).slice(0, 4);
  }, [visibleModules, moduleFrequency, queryDebounced]);
  const frequentEntities = React.useMemo(() => {
    const q_2 = normalize(queryDebounced);
    const recentEntitiesByKey = new Map<string, string>();
    for (const selection of recentSelections) {
      if (selection.kind !== "entity") continue;
      const key = entityKey(selection.moduleId, selection.entityId);
      if (!recentEntitiesByKey.has(key)) {
        recentEntitiesByKey.set(key, selection.label);
      }
    }
    return Object.entries(entityFrequency).map(([key_0, count]) => {
      if (count <= 0) return null;
      const separatorIndex = key_0.indexOf(":");
      if (separatorIndex <= 0) return null;
      const moduleId = key_0.slice(0, separatorIndex) as ModuleId;
      const entityId = key_0.slice(separatorIndex + 1);
      if (!entityId) return null;
      const searchModule = SEARCH_MODULES.find(m_2 => m_2.id === moduleId);
      if (!searchModule) return null;
      if (!searchModule.entityDestinations?.length) return null;
      if (searchModule.modulePermission && !canReadPermission(searchModule.modulePermission.path, searchModule.modulePermission.module)) {
        return null;
      }
      if (searchModule.entityPermission && !canReadPermission(searchModule.entityPermission.path, searchModule.entityPermission.module)) {
        return null;
      }
      const cachedEntityName = getCacheEntry(searchModule.id)?.items.find(entity => entity.id === entityId)?.name ?? "";
      const fallbackRecentLabel = recentEntitiesByKey.get(key_0) ?? "";
      const entityName = cachedEntityName || fallbackRecentLabel || "Untitled";
      if (q_2 && !(includesNormalized(entityName, q_2) || includesNormalized(searchModule.label, q_2) || includesNormalized(searchModule.description, q_2))) {
        return null;
      }
      return {
        module: searchModule,
        entity: {
          id: entityId,
          name: entityName
        },
        count
      };
    }).filter((item): item is {
      module: SearchModule;
      entity: SearchEntity;
      count: number;
    } => item !== null).sort((a_0, b_0) => {
      if (a_0.count !== b_0.count) return b_0.count - a_0.count;
      const entityNameCompare = (a_0.entity.name || "").localeCompare(b_0.entity.name || "");
      if (entityNameCompare !== 0) return entityNameCompare;
      return a_0.module.label.localeCompare(b_0.module.label);
    }).slice(0, FREQUENT_ENTITIES_LIMIT);
  }, [queryDebounced, recentSelections, entityFrequency, entityKey, canReadPermission, getCacheEntry]);

  // Fetch entities immediately after module selection.
  React.useEffect(() => {
    let cancelled = false;
    pruneExpired(NAV_SEARCH_TTL_MS);
    if (!open) return;
    if (!selectedModule) return;
    if (!selectedModule.fetchEntities || !selectedModule.mapEntities) return;
    if (selectedModule.entityPermission) {
      if (!canReadPermission(selectedModule.entityPermission.path, selectedModule.entityPermission.module)) {
        return;
      }
    }
    if (selectedModuleCachedCount > 0 && isCacheFresh(selectedModule.id, NAV_SEARCH_TTL_MS)) {
      return;
    }
    setLoading(true);
    setFetchError(null);
    (async () => {
      try {
        const res = await selectedModule.fetchEntities!();
        const mapped = selectedModule.mapEntities!(res).filter(e_0 => e_0.id).map(e_1 => ({
          id: e_1.id,
          name: e_1.name || ""
        }));
        if (cancelled) return;
        setCachedEntities(selectedModule.id, mapped);
      } catch (e: any) {
        if (cancelled) return;
        setFetchError(`Failed to load ${selectedModule.label}`);
      } finally {
        if (cancelled) return;
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedModule, selectedModuleCachedCount, canReadPermission, isCacheFresh, pruneExpired, setCachedEntities]);
  React.useEffect(() => {
    setEntityResultsLimit(ENTITY_RESULTS_BATCH_SIZE);
  }, [selectedModuleId, queryDebounced]);
  const filteredEntityResults = React.useMemo(() => {
    const q_3 = normalize(queryDebounced);
    if (!selectedModule) return [];
    if (selectedModule.entityPermission) {
      if (!canReadPermission(selectedModule.entityPermission.path, selectedModule.entityPermission.module)) {
        return [];
      }
    }
    const list = selectedModuleItems;
    if (!q_3) return list;
    return list.filter(e_2 => includesNormalized(e_2.name || "", q_3)).sort((a_1, b_1) => {
      const fa_0 = entityFrequency[entityKey(selectedModule.id, a_1.id)] ?? 0;
      const fb_0 = entityFrequency[entityKey(selectedModule.id, b_1.id)] ?? 0;
      if (fa_0 !== fb_0) return fb_0 - fa_0;
      return (a_1.name || "").localeCompare(b_1.name || "");
    });
  }, [queryDebounced, selectedModule, selectedModuleItems, canReadPermission, entityFrequency, entityKey]);
  const entityResults = React.useMemo(() => filteredEntityResults.slice(0, entityResultsLimit), [filteredEntityResults, entityResultsLimit]);
  const canShowMoreEntityResults = entityResults.length < filteredEntityResults.length;
  const handleResultsScroll = React.useCallback((e_3: React.UIEvent<HTMLDivElement>) => {
    if (!selectedModule || selectedEntity || !canShowMoreEntityResults) return;
    const el = e_3.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom > ENTITY_RESULTS_SCROLL_THRESHOLD) return;
    setEntityResultsLimit(currentLimit => Math.min(currentLimit + ENTITY_RESULTS_BATCH_SIZE, filteredEntityResults.length));
  }, [canShowMoreEntityResults, filteredEntityResults.length, selectedEntity, selectedModule]);
  const recentEntityResults = React.useMemo<RecentEntitySelection[]>(() => {
    if (!selectedModule || selectedEntity) return [];
    const q_4 = normalize(queryDebounced);
    if (q_4) return [];
    return recentSelections.filter((s_11): s_11 is RecentEntitySelection => s_11.kind === "entity" && s_11.moduleId === selectedModule.id).slice(0, 5);
  }, [recentSelections, selectedEntity, selectedModule, queryDebounced]);
  const resultGroups = React.useMemo(() => buildGlobalSearchGroups({
    branchId,
    selectedModule,
    selectedEntity,
    frequentModules,
    frequentEntities,
    visibleModules,
    recentEntityResults,
    entityResults,
    visibleEntityDestinations,
    query: queryDebounced,
    canShowMoreEntityResults
  }), [branchId, selectedModule, selectedEntity, frequentModules, frequentEntities, visibleModules, recentEntityResults, entityResults, visibleEntityDestinations, queryDebounced, canShowMoreEntityResults]);
  const stickyActionItems = React.useMemo(() => resultGroups.flatMap(group => group.items).filter((item_0): item_0 is Extract<SearchResultItem, {
    kind: "action";
  }> => item_0.kind === "action").slice(0, 3), [resultGroups]);
  const isSearching = open && query !== queryDebounced;
  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      focusInput();
    }
  }, [focusInput]);
  const hotkeyHint = React.useMemo(() => {
    if (open || selectedModule || selectedEntity) return undefined;
    return <span className="text-[11px] text-muted-foreground">
          {isMacLikePlatform ? "⌘K" : "Ctrl K"}
        </span>;
  }, [isMacLikePlatform, open, selectedEntity, selectedModule]);
  const navigate = React.useCallback((href: string) => {
    if (!href) return;
    setOpen(false);
    setQuery("");
    setSelectedModuleId(null);
    setSelectedEntity(null);
    setFetchError(null);
    setLoading(false);
    inputRef.current?.blur();
    router.push(href);
  }, [router]);
  const clearSelectedModule = React.useCallback(() => {
    setSelectedModuleId(null);
    setSelectedEntity(null);
    setQuery("");
    setFetchError(null);
    setLoading(false);
    focusInput();
  }, [focusInput]);
  const clearSelectedEntity = React.useCallback(() => {
    setSelectedEntity(null);
    setQuery("");
    focusInput();
  }, [focusInput]);
  const getEntityDefaultHref = React.useCallback((module_0: SearchModule, entityId_0: string) => {
    if (!branchId) return "";
    return module_0.entityHref?.(branchId, entityId_0) ?? module_0.entityDestinations?.[0]?.href(branchId, entityId_0) ?? module_0.landingHref(branchId);
  }, [branchId]);
  const selectModule = React.useCallback((module_1: SearchModule) => {
    bumpModule(module_1.id);
    const href_0 = branchId ? module_1.landingHref(branchId) : "";
    if (branchId) {
      pushRecent({
        kind: "module",
        moduleId: module_1.id,
        label: module_1.label,
        href: href_0,
        at: Date.now()
      });
    }
    if (!module_1.fetchEntities) {
      navigate(href_0);
      return;
    }
    setSelectedModuleId(module_1.id);
    setSelectedEntity(null);
    setQuery("");
    focusInput();
  }, [branchId, bumpModule, focusInput, navigate, pushRecent]);
  const selectEntity = React.useCallback((entity_0: SearchEntity) => {
    if (!selectedModule) return;
    bumpEntity(selectedModule.id, entity_0.id);
    const href_1 = getEntityDefaultHref(selectedModule, entity_0.id);
    if (branchId && href_1) {
      pushRecent({
        kind: "entity",
        moduleId: selectedModule.id,
        entityId: entity_0.id,
        label: entity_0.name || "Untitled",
        href: href_1,
        at: Date.now()
      });
    }
    if (entityDestinations.length > 0) {
      setSelectedEntity(entity_0);
      setQuery("");
      focusInput();
      return;
    }
    navigate(href_1);
  }, [branchId, bumpEntity, entityDestinations.length, getEntityDefaultHref, navigate, pushRecent, selectedModule, focusInput]);
  const selectFrequentEntity = React.useCallback((module_2: SearchModule, entity_1: SearchEntity) => {
    bumpModule(module_2.id);
    bumpEntity(module_2.id, entity_1.id);
    const href_2 = getEntityDefaultHref(module_2, entity_1.id);
    if (branchId && href_2) {
      pushRecent({
        kind: "entity",
        moduleId: module_2.id,
        entityId: entity_1.id,
        label: entity_1.name || "Untitled",
        href: href_2,
        at: Date.now()
      });
    }
    setSelectedModuleId(module_2.id);
    setSelectedEntity(entity_1);
    setQuery("");
    focusInput();
  }, [branchId, bumpEntity, bumpModule, focusInput, getEntityDefaultHref, pushRecent]);
  const selectEntityDestination = React.useCallback((destination_1: SearchEntityDestination) => {
    if (!branchId || !selectedModule || !selectedEntity) return;
    const href_3 = destination_1.href(branchId, selectedEntity.id);
    pushRecent({
      kind: "entity",
      moduleId: selectedModule.id,
      entityId: selectedEntity.id,
      label: selectedEntity.name || "Untitled",
      href: href_3,
      at: Date.now()
    });
    navigate(href_3);
  }, [branchId, navigate, pushRecent, selectedEntity, selectedModule]);
  const onSelectResult = React.useCallback((item_1: SearchResultItem) => {
    switch (item_1.kind) {
      case "module":
        selectModule(item_1.module);
        return;
      case "moduleOpen":
        if (!branchId) return;
        bumpModule(item_1.module.id);
        pushRecent({
          kind: "module",
          moduleId: item_1.module.id,
          label: item_1.module.label,
          href: item_1.module.landingHref(branchId),
          at: Date.now()
        });
        navigate(item_1.module.landingHref(branchId));
        return;
      case "entity":
      case "recentEntity":
        selectEntity(item_1.entity);
        return;
      case "frequentEntity":
        selectFrequentEntity(item_1.module, item_1.entity);
        return;
      case "destination":
        selectEntityDestination(item_1.destination);
        return;
      case "recentModule":
        bumpModule(item_1.module.id);
        pushRecent({
          kind: "module",
          moduleId: item_1.module.id,
          label: item_1.module.label,
          href: item_1.href,
          at: Date.now()
        });
        navigate(item_1.href);
        return;
      case "page":
        navigate(item_1.href);
        return;
      case "action":
        switch (item_1.actionId) {
          case "clear-query":
            setQuery("");
            focusInput();
            return;
          case "open-module-landing":
            if (!selectedModule || !branchId) return;
            navigate(selectedModule.landingHref(branchId));
            return;
          case "clear-selected-entity":
            clearSelectedEntity();
            return;
          case "clear-search-state":
            clearSelectedModule();
            return;
          default:
            return;
        }
      case "hint":
        return;
      default:
        {
          const exhaustiveCheck: never = item_1;
          return exhaustiveCheck;
        }
    }
  }, [branchId, bumpModule, navigate, pushRecent, focusInput, clearSelectedEntity, clearSelectedModule, selectedModule, selectEntity, selectFrequentEntity, selectEntityDestination, selectModule]);
  const onSelectResultWithRefocus = React.useCallback((item_2: SearchResultItem) => {
    onSelectResult(item_2);
    const shouldRefocusInput = item_2.kind === "action" || item_2.kind === "hint" || item_2.kind === "module" && Boolean(item_2.module.fetchEntities) || (item_2.kind === "entity" || item_2.kind === "recentEntity") && Boolean(selectedModule?.entityDestinations?.length) || item_2.kind === "frequentEntity";
    if (shouldRefocusInput) {
      focusInput();
    }
  }, [focusInput, onSelectResult, selectedModule?.entityDestinations?.length]);
  return <AsyncSearchCombobox ref={setInputRef} className={cn(className, "w-[18rem] transition-[width] duration-300 ease-out md:w-[22rem]", open && "w-[36rem] max-w-[calc(100vw-2rem)] md:w-[52rem] md:max-w-[52rem]")} value={query} placeholder={selectedEntity ? "Select a page…" : selectedModule ? "Search an item…" : placeholder} open={open} onOpenChange={handleOpenChange} onValueChange={setQuery} loading={loading} searching={isSearching} surfaceClassName="shadow-[0_18px_45px_-16px_hsl(var(--overlay)/0.35)] dark:shadow-[0_18px_45px_-16px_hsl(var(--overlay)/0.7)]" emptyMessage={fetchError ?? "No results found."} inputAddonAfter={hotkeyHint} addonBefore={selectedModule ? <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground">
                {selectedModule.label}
              </span>
              {selectedEntity && <>
                  <span className="text-xs text-muted-foreground">/</span>
                  <span className="max-w-28 truncate text-xs font-medium text-foreground">
                    {selectedEntity.name || "Untitled"}
                  </span>
                </>}
              <button type="button" className="inline-flex items-center justify-center rounded-md p-1 hover:bg-muted" onClick={e_4 => {
      e_4.preventDefault();
      e_4.stopPropagation();
      if (selectedEntity) {
        clearSelectedEntity();
        return;
      }
      clearSelectedModule();
    }} aria-label={selectedEntity ? "Clear entity" : "Clear module"}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div> : undefined} onInputKeyDown={e_5 => {
    if (e_5.key === "Escape") {
      setOpen(false);
    }
    if (e_5.key === "Backspace" && selectedEntity && normalize(query) === "") {
      clearSelectedEntity();
      return;
    }
    if (e_5.key === "Backspace" && selectedModule && normalize(query) === "") {
      clearSelectedModule();
    }
  }} onListScroll={handleResultsScroll} footer={stickyActionItems.length > 0 ? <div className="flex flex-wrap items-center gap-2">
              {stickyActionItems.map(item_3 => <StickyActionButton key={`sticky-${item_3.id}`} actionId={item_3.actionId} label={item_3.label} onClick={() => onSelectResultWithRefocus(item_3)} />)}
            </div> : undefined}>
        {resultGroups.map(group_0 => <CommandGroup key={group_0.id} heading={group_0.heading}>
            {group_0.items.map(item_4 => <CommandItem key={item_4.id} value={item_4.value} disabled={Boolean(item_4.disabled)} className={item_4.className} onMouseDown={event_0 => event_0.preventDefault()} onSelect={() => onSelectResultWithRefocus(item_4)}>
                <div className="flex flex-col">
                  <span>
                    {isHintResultItem(item_4) ? item_4.label : <HighlightMatch text={item_4.label} query={queryDebounced} />}
                  </span>
                  {item_4.description && <span className="text-xs text-muted-foreground">
                      <HighlightMatch text={item_4.description} query={queryDebounced} />
                    </span>}
                </div>
              </CommandItem>)}
          </CommandGroup>)}
      </AsyncSearchCombobox>;
});
GlobalSearch.displayName = "GlobalSearch";
function StickyActionButton(t0) {
  const $ = _c(9);
  const {
    actionId,
    label,
    onClick
  } = t0;
  let t1;
  if ($[0] !== actionId) {
    t1 = getStickyActionMeta(actionId);
    $[0] = actionId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const meta = t1;
  const Icon = meta.icon;
  const t2 = `inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${meta.className}`;
  let t3;
  if ($[2] !== Icon) {
    t3 = <Icon className="h-3.5 w-3.5" />;
    $[2] = Icon;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== label || $[5] !== onClick || $[6] !== t2 || $[7] !== t3) {
    t4 = <button type="button" className={t2} onMouseDown={_temp} onClick={onClick}>{t3}{label}</button>;
    $[4] = label;
    $[5] = onClick;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp(event) {
  return event.preventDefault();
}
function useDebouncedValue(value, delayMs) {
  const $ = _c(4);
  const [debounced, setDebounced] = React.useState(value);
  let t0;
  let t1;
  if ($[0] !== delayMs || $[1] !== value) {
    t0 = () => {
      const id = window.setTimeout(() => setDebounced(value), delayMs);
      return () => window.clearTimeout(id);
    };
    t1 = [value, delayMs];
    $[0] = delayMs;
    $[1] = value;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  React.useEffect(t0, t1);
  return debounced;
}